<template>
    <vue-scroll>
        <div class="page-header">
            <h1>{{ current_community.name }}</h1>
        </div>
        <template v-if="previousOrderNotInvoiced">
            <el-alert
                :closable="false"
                :title="
                    'Order for ' +
                    previousOrderNotInvoiced.month.format('MMMM YYYY') +
                    ' issue is still processing.'
                "
                center
                class="text-center"
                show-icon
                style="max-width: 800px; margin: 0 auto"
                type="info"
            >
            </el-alert>
        </template>
        <template v-else>
            <template
                v-if="
                    !orderSubmittedForMonth ||
                    (current_order && current_order.is_billable_edit)
                "
            >
                <calendars
                    v-if="calendarEnabled && can(Permissions.CREATE_CALENDAR)"
                />
                <newsletters
                    class="mt-20"
                    v-if="
                        newsletterEnabled && can(Permissions.CREATE_NEWSLETTER)
                    "
                />
            </template>
            <div class="mt-60" v-else>
                <el-alert
                    :closable="false"
                    :title="
                        'Order has been submitted for ' +
                        currentMonth.format('MMMM YYYY') +
                        ' issue.'
                    "
                    center
                    class="text-center"
                    show-icon
                    style="max-width: 800px; margin: 0 auto"
                    type="success"
                >
                </el-alert>
            </div>
        </template>
        <div class="mt-20">
            <div class="header-with-line">
                <img src="/images/dashboard/announcements.svg" />
                <h2>Announcements</h2>
                <div class="line"></div>
            </div>
            <el-table
                class="el-table--no-border el-table--no-header"
                :data="current_community.past_due_orders"
                v-if="current_community.has_past_due_invoice"
            >
                <el-table-column>
                    <template slot-scope="{ row }">
                        <p
                            class="text-error mb-0 mt-0"
                            v-if="current_community.ignore_past_due"
                        >
                            Your {{ row.formatted_issue }} invoice is pending
                            payment.
                        </p>
                        <p
                            class="text-error mb-0 mt-0"
                            v-if="!current_community.ignore_past_due"
                        >
                            You have a payment overdue for your
                            {{ row.formatted_issue }} issue.
                        </p>
                        <p
                            class="mb-0 mt-0"
                            v-if="
                                !current_community.ignore_past_due &&
                                current_community.suspended
                            "
                        >
                            Please make a payment to place your
                            {{ currentMonth.format("MMMM YYYY") }} order.
                        </p>
                    </template>
                </el-table-column>
                <el-table-column>
                    <template slot-scope="{ row }">
                        <div class="text-right">
                            <el-button
                                @click="viewInvoice(row)"
                                icon="mdi mdi-eye"
                                round
                                type="primary"
                                v-if="!row.has_sage_invoice"
                                >View Order Confirmation</el-button
                            >
                            <el-button
                                @click="viewSageInvoice(row)"
                                icon="mdi mdi-eye"
                                round
                                type="primary"
                                v-if="row.has_sage_invoice"
                                >View Invoice</el-button
                            >
                            <el-button
                                @click="getPaymentUrl()"
                                icon="mdi mdi-credit-card"
                                round
                                type="danger"
                                style="padding-left: 10px"
                                v-if="
                                    row.has_sage_invoice &&
                                    !row.is_paid &&
                                    !current_community.is_bill_to_account_only
                                "
                                >Pay Now
                            </el-button>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
            <p
                v-html="current_community.corporation.announcement"
                v-if="
                    current_community.corporation &&
                    current_community.corporation.announcement
                "
            ></p>
            <p
                v-html="current_community.announcement"
                v-if="current_community.announcement"
            ></p>
            <p
                v-for="notification in custom_notifications"
                :key="notification.id"
                v-html="notification.body"
            ></p>
            <template>
                <div v-for="order in pendingOrders" :key="order.id">
                    <el-alert
                        class="mb-20 el-alert-content-center"
                        v-if="order.order.is_billable_edit"
                        title=""
                        type="warning"
                        :closable="false"
                    >
                        <h4 class="m-0 text-center">
                            Your order for {{ order.month | month_year }} is
                            placed on billable edit.
                        </h4>
                    </el-alert>
                </div>
            </template>
            <template v-if="previous_order">
                <template
                    v-if="
                        !$store.state.config.settings.disable_order_download &&
                        previous_order.approval_status ===
                            ApprovalStatus.APPROVED &&
                        previous_order.has_downloadable_calendar &&
                        (can(Permissions.CREATE_CALENDAR) ||
                            can(Permissions.CREATE_NEWSLETTER))
                    "
                >
                    <el-alert
                        :closable="false"
                        class="mb-20 el-alert-content-center"
                        title=""
                        type="success"
                    >
                        <h4 class="m-0 text-center">
                            Your digital calendar for
                            {{ previous_order.formatted_issue }} issue is ready
                            to download.
                        </h4>
                    </el-alert>
                    <div>
                        <el-button
                            :loading="downloading_previous_order"
                            @click="downloadCalendars(previous_order)"
                            icon="mdi mdi-download"
                            type="primary"
                        >
                            Digital Download
                        </el-button>
                    </div>
                </template>
            </template>

            <div class="mt-20">
                <a
                    class="el-button el-button--corwin-green el-button--large el-button--default is-round"
                    href="https://www.corwindesign.com/tutorials/"
                    style="width: 280px; text-decoration: none"
                    target="_blank"
                >
                    Tutorials <i class="mdi mdi-flower" />
                </a>
            </div>
        </div>
    </vue-scroll>
</template>

<script>
import * as ApprovalStatus from "@/constants/approval-status";
import CustomNotificationType from "@/constants/custom-notification-types";
import * as OrderStatus from "@/constants/order-statuses";
import auth from "@/mixins/auth";
import * as Actions from "@/store/constants/actions";
import FileUtils from "@/utils/file";
import OrderUtils from "@/utils/order";
import { mapGetters, mapState } from "vuex";
import Calendars from "./dashboard/Calendars";
import Newsletters from "./dashboard/Newsletters";
import WeeklyCalendars from "./dashboard/WeeklyCalendars";

export default {
    components: {
        Calendars,
        WeeklyCalendars,
        Newsletters
    },
    mixins: [auth],
    data() {
        return {
            ApprovalStatus,
            items: {
                data: []
            },
            previous_order: null,
            downloading_previous_order: false,
            custom_notifications: []
        };
    },
    computed: {
        ...mapGetters([
            "newsletterEnabled",
            "calendarEnabled",
            "weeklyCalendarEnabled",
            "currentMonth"
        ]),
        ...mapState({
            orderSubmittedForMonth: (state) =>
                state.current_community.order_submitted_for_month
        }),
        ...mapState(["current_order", "current_community"]),
        pendingOrders() {
            return this.$store.state.orders.data.map((order) => {
                let month = moment([order.year, order.month - 1]);
                return {
                    order,
                    month,
                    has_downloadable: this.hasDownloadable(order)
                };
            });
        },
        previousOrderNotInvoiced() {
            if (!this.pendingOrders.length) {
                return null;
            }
            let orders = this.pendingOrders.filter((o) => {
                return (
                    o.month.format("YYYY-MM") <
                        this.currentMonth.format("YYYY-MM") &&
                    !_.find(o.order.statuses.data, {
                        status: OrderStatus.INVOICED,
                        checked: true
                    })
                );
            });
            if (!orders.length) {
                return null;
            }
            return _.values(
                _.orderBy(orders, (o) => o.month.format("YYYY-MM"), ["desc"])
            )[0];
        }
    },
    created() {
        this.fetchItems();
        this.fetchPreviousOrder();
        this.getCustomNotifications();
        this.$store.dispatch(Actions.FETCH_CURRENT_COMMUNITY);
        this.$store.dispatch(Actions.FETCH_CURRENT_ORDER);
        this.$store.dispatch(Actions.FETCH_ORDERS);
    },
    methods: {
        fetchPreviousOrder() {
            axios.get(`/web-api/order/my/previous`).then((response) => {
                if (!response.data.id) {
                    return;
                }
                this.previous_order = response.data;
            });
        },
        formatAnnouncement(announcement) {
            return Utils.convertTextToHtml(announcement);
        },
        hasDownloadable(order) {
            return (
                order.has_downloadable_calendar_pdf ||
                order.has_downloadable_calendar_jpg
            );
        },
        fetchItems() {
            axios.get(`/web-api/calendar`).then((response) => {
                this.items.data = response.data.data;
            });
        },
        editItem(item) {
            this.$router.push({
                name: "EditCalendar",
                params: {
                    id: item.id
                }
            });
        },
        downloadCalendars(order) {
            this.downloading_previous_order = true;
            axios
                .get(`/web-api/order/${order.id}/download-calendars`, {
                    responseType: "blob"
                })
                .then((response) => {
                    FileUtils.downloadFileFromBlobResponse(response);
                })
                .finally(() => (this.downloading_previous_order = false));
        },
        gotoBlog() {
            window.open(`https://www.corwindesign.com/blog/`, "_blank");
        },
        getCustomNotifications() {
            axios
                .get(`/web-api/custom-notification/for-me`, {
                    params: {
                        type: CustomNotificationType.announcement.value,
                        all: 1
                    }
                })
                .then((response) => {
                    this.custom_notifications = response.data.data;
                });
        },
        viewInvoice(order) {
            OrderUtils.openInvoicePdf(order);
        },
        viewSageInvoice(order) {
            OrderUtils.openSageInvoicePdf(order);
        },
        async getPaymentUrl() {
            try {
                const response = await fetch(`/web-api/payment-url`);
                const data = await response.json();
                const paymentUrl = data.payment_url;
                window.open(`${paymentUrl}`, "_blank");
            } catch (error) {
                console.error("Error copying payment URL:", error);
            }
        }
    }
};
</script>

<style scoped></style>
